import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Typography, makeStyles } from '@material-ui/core';
import { ViewSummaryButton } from './ViewSummaryButton';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(1, 0, 0, 1)
  },
  rateCountLine: {
    fontWeight: theme.typography.fontWeightMedium
  },
  divider: {
    marginLeft: theme.spacing(0.75)
  },
  definedByLine: {
    marginTop: theme.spacing(0.5)
  },
  infoText: {
    marginTop: theme.spacing(1.5),
    fontStyle: 'italic',
    color: theme.palette.text.secondary
  },
  emptyText: {
    fontStyle: 'italic',
    color: theme.palette.text.secondary
  }
}));

export const ProjectCostOverridesReadOnlyContent = ({
  projectRateCount = 0,
  projectDimensions = [],
  onViewSummary
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const dimensionsLabel = useMemo(() => {
    if (projectDimensions.length === 0) {
      return formatMessage({ id: 'projectCostOverrides.summary.defaultRate' });
    }

    return projectDimensions
      .map(d => d.displayText)
      .join(formatMessage({ id: 'projectCostOverrides.summary.separator' }));
  }, [formatMessage, projectDimensions]);

  const rateCountValues = useMemo(() => ({ count: projectRateCount }), [
    projectRateCount
  ]);
  const dimensionsValues = useMemo(() => ({ dimensions: dimensionsLabel }), [
    dimensionsLabel
  ]);

  if (projectRateCount === 0) {
    return (
      <div className={classes.root}>
        <Typography
          variant="body2"
          className={classes.emptyText}
          data-qe-id="ProjectCostOverridesNoOverridesSet"
        >
          <FormattedMessage id="projectCostOverrides.noCostRateOverridesSet" />
        </Typography>
        <Typography
          variant="body2"
          className={classes.emptyText}
          data-qe-id="ProjectCostOverridesCostsBasedOnUser"
        >
          <FormattedMessage id="projectCostOverrides.projectCostsBasedOnUser" />
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Typography
        variant="body2"
        className={classes.rateCountLine}
        data-qe-id="ProjectCostOverridesRateCount"
      >
        <FormattedMessage
          id="projectCostOverrides.summary.ratesSet"
          values={rateCountValues}
        />
        <span className={classes.divider}>&ndash;</span>
      </Typography>
      <Typography
        variant="body2"
        className={classes.definedByLine}
        data-qe-id="ProjectCostOverridesDefinedByDimensions"
      >
        <FormattedMessage
          id="projectCostOverrides.summary.definedByDimensions"
          values={dimensionsValues}
        />
      </Typography>
      <Typography
        variant="body2"
        className={classes.infoText}
        data-qe-id="ProjectCostOverridesSummaryInfoText"
      >
        <FormattedMessage id="projectCostOverrides.summary.infoText" />
      </Typography>
      {onViewSummary ? <ViewSummaryButton onClick={onViewSummary} /> : null}
    </div>
  );
};

ProjectCostOverridesReadOnlyContent.propTypes = {
  projectRateCount: PropTypes.number,
  projectDimensions: PropTypes.array,
  onViewSummary: PropTypes.func
};

export default ProjectCostOverridesReadOnlyContent;
